import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HelpChatWidget.css';

interface Question {
  id: string;
  question: string;
  answers?: string[];
  followUpQuestions?: Question[];
}

const WHATSAPP_NUMBER = '+61426909106';
const WHATSAPP_MESSAGE = 'Hi, I need help with TaskWombat services.';

const mainQuestions: Question[] = [
  {
    id: 'services',
    question: 'What services do you offer?',
    answers: [
      'We offer two main categories of services:',
      '1. Commercial Cleaning - Office cleaning, strata, facilities, hospitality venues, and long-term contracts',
      '2. Residential Cleaning - Regular cleaning, end of lease, Airbnb, deep cleaning, and more',
      '',
      'We are fully insured with $10M public liability coverage and use eco-friendly products.'
    ],
    followUpQuestions: [
      {
        id: 'cleaning-details',
        question: 'Tell me about cleaning services',
        answers: [
          'Our cleaning services include:',
          '• Commercial Cleaning Contracts (offices, strata, facilities)',
          '• Regular House Cleaning (weekly, fortnightly, monthly)',
          '• End of Lease Cleaning (bond-back guaranteed)',
          '• Airbnb Cleaning (2-4 hour turnaround)',
          '• Deep Cleaning',
          '• Carpet & Upholstery Cleaning',
          '• Window Cleaning',
          '• Oven & Appliance Cleaning',
          '• NDIS Cleaning Services',
          '',
          'All services use eco-friendly products and include satisfaction guarantee.'
        ]
      }
    ]
  },
  {
    id: 'pricing',
    question: 'How much do services cost?',
    answers: [
      'Our pricing varies based on the service type and property size. We offer transparent, upfront pricing with no hidden fees.',
      '',
      '• Get an instant quote on our website',
      '• Pricing depends on service type, property size, and frequency',
      '• We currently have exclusive seasonal offers available',
      '• Same-day service may have additional charges',
      '',
      'Visit our quote page for a personalized estimate.'
    ],
    followUpQuestions: [
      {
        id: 'get-quote',
        question: 'How do I get a quote?',
        answers: [
          'Getting a quote is easy:',
          '1. Visit our quote page',
          '2. Select your service type',
          '3. Choose property size/options',
          '4. Enter your details',
          '5. Receive an instant quote',
          '',
          'You can also request a custom quote for special requirements.'
        ]
      },
      {
        id: 'discounts',
        question: 'Do you offer discounts?',
        answers: [
          'Yes! We offer:',
          '• Exclusive seasonal offers',
          '• Regular maintenance discounts',
          '• Package deals for multiple services',
          '• First-time customer specials',
          '',
          'Contact us to learn about current promotions.'
        ]
      }
    ]
  },
  {
    id: 'booking',
    question: 'How do I book a service?',
    answers: [
      'Booking is simple:',
      '1. Request a quote through our website',
      '2. Choose your preferred date and time',
      '3. Confirm your booking',
      '4. Our team will arrive on time',
      '',
      'We offer flexible scheduling including same-day service for urgent needs.'
    ],
    followUpQuestions: [
      {
        id: 'same-day',
        question: 'Do you offer same-day service?',
        answers: [
          'Yes! We offer same-day service for most cleaning services, subject to availability.',
          '',
          'Same-day bookings are best for:',
          '• Regular cleaning',
          '• Deep cleaning',
          '• Carpet cleaning',
          '',
          'Contact us as early as possible to secure same-day service.'
        ]
      },
      {
        id: 'reschedule',
        question: 'Can I reschedule or cancel?',
        answers: [
          'Yes, you can reschedule or cancel your booking. Please contact us at least 24 hours in advance to avoid any cancellation fees.',
          '',
          'For urgent changes, please contact us directly and we\'ll do our best to accommodate your request.'
        ]
      }
    ]
  },
  {
    id: 'coverage',
    question: 'What areas do you service?',
    answers: [
      'We currently serve:',
      '• Tarneit',
      '• Hoppers Crossing',
      '• Point Cook',
      '• Williams Landing',
      '• Brighton (3186)',
      '• Malvern (3144)',
      '• Malvern East (3145)',
      '• Hawthorn (3122)',
      '• Werribee',
      '• Truganina',
      '• Laverton',
      '• Altona Meadows',
      '• Serving across Australia',
      '',
      'Contact us to check if we service your area.'
    ]
  },
  {
    id: 'insurance',
    question: 'Are you insured?',
    answers: [
      'Yes! We are fully insured with comprehensive $10M public liability coverage. This means you\'re fully protected in case of any accidental damage or injury during service.',
      '',
      'All our team members are:',
      '• Background-checked',
      '• Fully trained',
      '• Fully insured and bonded',
      '',
      'Your peace of mind is our priority.'
    ]
  },
  {
    id: 'guarantee',
    question: 'Do you offer a satisfaction guarantee?',
    answers: [
      'Yes! We offer a 100% satisfaction guarantee on all services.',
      '',
      'If you\'re not completely satisfied:',
      '• Contact us within 24 hours',
      '• We\'ll return to fix it at no extra cost',
      '• Your satisfaction is our commitment',
      '',
      'We stand behind the quality of our work.'
    ]
  },
  {
    id: 'products',
    question: 'What cleaning products do you use?',
    answers: [
      'We use only eco-friendly, non-toxic cleaning products that are:',
      '• Safe for your family',
      '• Safe for pets',
      '• Safe for the environment',
      '• Effective at cleaning',
      '',
      'All products meet Australian safety standards and are pet and child-friendly.'
    ]
  }
];

const HelpChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [chatHistory, setChatHistory] = useState<Array<{ type: 'bot' | 'user'; content: string }>>([]);
  const [showMainQuestions, setShowMainQuestions] = useState(true);

  const handleQuestionClick = (question: Question) => {
    setChatHistory(prev => [...prev, { type: 'user', content: question.question }]);
    
    if (question.answers) {
      setChatHistory(prev => [...prev, { type: 'bot', content: question.answers!.join('\n') }]);
    }
    
    setCurrentQuestion(question);
    setShowMainQuestions(false);
  };

  const handleBackToMain = () => {
    setCurrentQuestion(null);
    setShowMainQuestions(true);
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(WHATSAPP_MESSAGE);
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset chat state after animation
    setTimeout(() => {
      setChatHistory([]);
      setCurrentQuestion(null);
      setShowMainQuestions(true);
    }, 300);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        className="help-chat-button"
        onClick={() => setIsOpen(true)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <img 
          src="/customer-support-representative.png" 
          alt="Customer Support" 
          className="chat-button-image"
        />
        <span className="chat-button-text">Need Help?</span>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="help-chat-window"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            {/* Chat Header */}
            <div className="chat-header">
              <div className="chat-header-info">
                <span className="material-icons">support_agent</span>
                <div>
                  <h3>Help & Support</h3>
                  <p>We're here to help!</p>
                </div>
              </div>
              <button className="chat-close-button" onClick={handleClose}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Chat Body */}
            <div className="chat-body">
              {showMainQuestions ? (
                <div className="main-questions-container">
                  <div className="welcome-message">
                    <p>Hi! How can we help you today?</p>
                  </div>
                  <div className="questions-list">
                    {mainQuestions.map((question) => (
                      <motion.button
                        key={question.id}
                        className="question-button"
                        onClick={() => handleQuestionClick(question)}
                        whileHover={{ scale: 1.02, x: 5 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="material-icons">help_outline</span>
                        {question.question}
                      </motion.button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="chat-messages">
                  {chatHistory.map((message, index) => (
                    <motion.div
                      key={index}
                      className={`chat-message ${message.type}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="message-content">
                        {message.content.split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < message.content.split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </div>
                    </motion.div>
                  ))}

                  {currentQuestion?.followUpQuestions && (
                    <div className="follow-up-questions">
                      <p className="follow-up-title">Related questions:</p>
                      {currentQuestion.followUpQuestions.map((question) => (
                        <motion.button
                          key={question.id}
                          className="follow-up-button"
                          onClick={() => handleQuestionClick(question)}
                          whileHover={{ scale: 1.02, x: 5 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <span className="material-icons">arrow_forward</span>
                          {question.question}
                        </motion.button>
                      ))}
                    </div>
                  )}

                  <div className="chat-actions">
                    <motion.button
                      className="back-button"
                      onClick={handleBackToMain}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="material-icons">arrow_back</span>
                      Back to Main Menu
                    </motion.button>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Footer */}
            <div className="chat-footer">
              <motion.button
                className="whatsapp-button"
                onClick={handleWhatsApp}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="material-icons">chat</span>
                Chat with Live Agent via WhatsApp
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HelpChatWidget;

